<template>
  <form
    class="email-verification-popup"
    @submit.prevent="onFormSubmit">
    <button
      v-if="isButtonVisible"
      class="email-verification-popup__close-button shape-cross"
      :disabled="!canClose || !data.isClosable"
      @click="onVerifyLater" />

    <picture class="email-verification-popup__image">
      <source srcset="/images/client/email-verification@2x.png 2x, /images/client/email-verification.png 1x">
      <img
        src="/images/client/email-verification.png"
        alt="Verify email">
    </picture>

    <h2 class="email-verification-popup__title">
      {{ currentView.title }}
    </h2>

    <p class="email-verification-popup__message">
      {{ currentView.message }}
    </p>

    <ui-input
      v-model="newEmail"
      placeholder="Email"
      name="email"
      inputmode="email"
      autocomplete="email"
      class="email-verification-popup__input"
      :class="{'email-verification-popup__input--disabled': viewKey !== 'CHANGE_EMAIL'}"
      :z-index-tooltip="2147483011"
      :disabled="viewKey !== 'CHANGE_EMAIL'"
      :error="errors.email"

      focus-on-mount
      @input="errors.email = false" />

    <div
      v-if="viewKey !== 'LINK_SENT' && !data.hasPassword"
      class="email-verification-popup__checkboxes">
      <ui-checkbox v-model="isMarketingChecked">
        <span class="email-verification-popup__checkbox-text">
          I want subscriber-only deals, discounts, and promo codes
        </span>
      </ui-checkbox>

      <ui-checkbox v-model="isTermsChecked">
        <span class="email-verification-popup__checkbox-text">
          I have read and agree to the
          <a
            href="/agreement"
            target="_blank"
            class="email-verification-popup__link">
            Terms of Service
          </a>
        </span>
      </ui-checkbox>
    </div>

    <div class="email-verification-popup__buttons">
      <button
        class="button button-main email-verification-popup__button"
        type="submit"
        :disabled="isSendButtonDisabled">
        {{ isEmailSent ? coolDownTimeText : currentView.button }}
      </button>

      <button
        v-if="canShowSkipButtons"
        class="button button-bordered email-verification-popup__button"
        :class="{
          'button--disabled_bordered': !canClose,
          'button-bordered--main': canClose
        }"
        type="button"
        :disabled="!canClose"
        @click="onVerifyLater">
        Cancel and verify later
      </button>

      <button
        v-if="viewKey !== 'CHANGE_EMAIL'"
        class="button email-verification-popup__button"
        type="button"
        @click="onEmailChange">
        Change email
      </button>

      <span
        v-if="viewKey === 'LINK_SENT'"
        class="email-verification-popup__support">
        Need help? Contact our <a @click="onSupportClick">Support</a>
      </span>
    </div>
  </form>
</template>

<script>
import UiInput from 'Components/ui/Input/UiInput.vue';
import UiCheckbox from 'Components/ui/Checkbox/UiCheckbox.vue';
import { regExpEmail } from 'Root/helpers/regexps';
import AppModel from 'Models/AppModel';
import yandexMetrika from 'Root/drivers/yandexMetrika';
import UserModel from 'Models/UserModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import { mapState, mapActions } from 'vuex';

const VIEWS = {
  SEND_LINK: {
    title: 'Verify your email to activate your account',
    message: 'Please confirm your email and we’ll send a verification link',
    button: ' Save and send link',
  },

  LINK_SENT: {
    title: 'Verification link sent!',
    // eslint-disable-next-line max-len
    message: 'Don’t see it? Check your spam folder or try resending the link',
    button: 'Resend link',
  },

  CHANGE_EMAIL: {
    title: 'Change email',
    message: 'Enter your email and we’ll send a verification link',
    button: ' Save and send link',
  },
};

export default {
  name: 'EmailVerificationPopup',
  components: { UiCheckbox, UiInput },

  props: {
    /**
     * {
     *   email: string,
     *   isMarketingEmailsAllowed: boolean,
     *   hasPassword: boolean,
     * }
     */
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      viewKey: 'SEND_LINK',
      newEmail: this.data.email,
      errors: {
        email: false,
      },
      isTermsChecked: this.data.hasPassword,
      isMarketingChecked: this.data.isMarketingEmailsAllowed,
      isEmailSent: false,
      hasAttemptedVerification: false,

      coolDownInterval: null,
      coolDownTime: 60,
    };
  },

  computed: {
    ...mapState(['userReactiveStates']),
    canClose() {
      return !this.isEmailSent || this.coolDownTime === 0;
    },

    currentView() {
      return VIEWS[this.viewKey];
    },

    isSendButtonDisabled() {
      return !this.isTermsChecked || this.isEmailSent;
    },

    coolDownTimeText() {
      return `00:${this.coolDownTime.toString().padStart(2, '0')}`;
    },

    canShowSkipButtons() {
      return this.hasAttemptedVerification;
    },

    isButtonVisible() {
      return this.canShowSkipButtons || this.data.isClosable;
    },
  },

  mounted() {
    yandexMetrika.invokeEvent('email_verify_view');
  },

  methods: {
    ...mapActions(['getUser']),

    onFormSubmit() {
      if (!this.newEmail.match(regExpEmail)) {
        this.errors.email = 'Invalid email';
        return;
      }

      if (this.isTermsChecked) {
        yandexMetrika.invokeEvent('email_verify_terms_ok');
      }

      if (this.isMarketingChecked) {
        yandexMetrika.invokeEvent('email_verify_marketing_ok');
      }

      this.$axios.post(`${AppModel.API_URI}/user/send_verification_link`, {
        email: this.newEmail,
        is_marketing_emails_allowed: this.isMarketingChecked,
      }).then((response) => {
        this.hasAttemptedVerification = true;
        this.viewKey = 'LINK_SENT';

        this.isEmailSent = true;
        this.coolDownInterval = setInterval(() => {
          if (this.coolDownTime === 0) {
            clearInterval(this.coolDownInterval);
            this.coolDownInterval = null;
            this.coolDownTime = 60;
            this.isEmailSent = false;
            return;
          }

          this.coolDownTime -= 1;
        }, 1000);

        if (response.data.skycoach_token !== null) {
          localStorage.setItem(UserModel.SET_PASSWORD_TOKEN_NAME, response.data.skycoach_token);

          this.getUser();
        }
      }).catch((error) => {
        if (error.response.status === 400) {
          this.errors.email = error.response.data.message;
        } else if (error.response.status === 429) {
          this.errors.email = 'Verification link was sent recently. Please wait a bit and try again';
        } else {
          this.errors.email = 'Something went wrong';
        }
      });
      yandexMetrika.invokeEvent('email_verify_send');
    },

    onEmailChange() {
      this.viewKey = 'CHANGE_EMAIL';
      yandexMetrika.invokeEvent('email_verify_edit');
    },

    onVerifyLater() {
      if (!this.canClose) {
        return;
      }

      localStorage.setItem(UserModel.EMAIL_VERIFY_LATER_TOKEN_NAME, 'true');
      this.userReactiveStates.email_verify_later = true;
      this.$vuedals.close();
      yandexMetrika.invokeEvent('email_verify_later');
    },

    onSupportClick() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },
  },
};
</script>

<style lang="scss">
  .email-verification-popup .mobile-panel-popup__body {
    padding: 0;
  }

  .email-verification-popup {
    .mobile-panel-popup__head {
      display: none;
    }
  }

  .sign-in-popup.mobile-panel-popup {
    background-color: $color--basic-black;
  }

  @media screen and #{$screen-more-or-640} {
    .email-verification-popup > .vuedal {
      width: auto !important;
    }
  }
</style>

<style scoped lang="scss">
  .email-verification-popup {
    position: relative;
    display: grid;
    grid-gap: 24px;
    width: 485px;
    padding: 32px 40px;
    border-radius: 15px;
  }

  .email-verification-popup__image {
    justify-self: center;
    width: 188px;
    height: 110px;
  }

  .email-verification-popup__title {
    font-size: 25px;
    line-height: 29px;
    font-weight: 500;
    text-align: center;
    max-width: 300px;
    justify-self: center;
  }

  .email-verification-popup__message {
    font-size: 15px;
    line-height: 17px;
    text-align: center;
  }

  .email-verification-popup__input {
    --background-color: #{$color--basic-dark-gray} !important;
    --height: 48px !important;
    --padding: 15px !important;
    --border-radius: 5px !important;
    --font-size: 15px !important;
    --font-weight: 500 !important;

    /deep/ input:disabled {
      color: #A8BBDB;
    }
  }

  .email-verification-popup__input--disabled {
      opacity: .5;
  }

  .email-verification-popup__checkboxes {
    display: grid;
    grid-gap: 16px;
  }

  .email-verification-popup__close-button {
    position: absolute;
    width: 10px;
    height: 10px;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    z-index: 1;
  }

  .email-verification-popup__close-button:disabled {
    cursor: not-allowed;
  }

  .email-verification-popup__checkbox-text {
    font-size: 13px;
    line-height: 16px;
    font-weight: 500;
  }

  .email-verification-popup__link {
    font-size: 13px;
    line-height: 16px;
    font-weight: 500;
    color: $color--basic-white;
    text-decoration: underline;
  }

  .email-verification-popup__buttons {
    display: grid;
    grid-gap: 8px;
    justify-content: center;
  }

  .email-verification-popup__button {
    width: max-content;
    min-width: 80px;
    padding: 15px;
    font-size: 15px;
    margin: 0 auto;
  }

  .email-verification-popup__support {
    color: rgba(255, 255, 255, 0.5);
    font-size: 13px;
    line-height: 16px;

    a {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  @media screen and #{$screen-less-than-640} {
    .email-verification-popup {
      width: 100%;
      grid-gap: 16px;
      padding: 32px 20px;
    }

    .email-verification-popup__buttons {
      justify-content: unset;
    }

    .email-verification-popup__button {
      display: flex;
      justify-content: center;
      width: 100%;
      padding: 12px;
    }
  }
</style>
